/**
 * deviceReport.ts — fire-and-forget telemetry reporter that captures the
 * browser/device a child is using so the sysadmin console can see "which
 * iPad/PWA model is in front of this kid right now". Observational only.
 *
 * 调用关系：
 *   - App.tsx calls reportCurrentDeviceSoon() once on mount (after login)
 *   - POST /api/v1/child/devices implemented in backend/src/routes/child.js
 *   - device_key persists per browser in localStorage so multiple shared
 *     iPads can be distinguished without identifying the human
 *
 * 关键约束：
 *   - Throttled to once every REPORT_INTERVAL_MS per browser so we don't
 *     spam the backend on every reload.
 *   - Errors are swallowed — telemetry must never interrupt gameplay.
 *   - Browser-only (touches window.navigator, localStorage, crypto.randomUUID).
 */
import { api } from '../api/client';

const DEVICE_KEY = 'child_device_key_v1';
const LAST_REPORT_KEY = 'child_device_last_report_v1';
/** Resend the device snapshot at most every 6h — captures meaningful changes
 *  (orientation, OS update, new browser) without flooding the backend. */
const REPORT_INTERVAL_MS = 6 * 60 * 60_000;
/** Delay (ms) before the actual POST so initial login traffic settles first. */
const REPORT_DEFER_MS = 900;

export function reportCurrentDeviceSoon() {
  if (typeof window === 'undefined') return;
  const last = Number(window.localStorage.getItem(LAST_REPORT_KEY) || 0);
  if (Date.now() - last < REPORT_INTERVAL_MS) return;
  window.setTimeout(() => {
    const payload = collectDeviceInfo();
    api.reportDevice(payload)
      .then(() => {
        window.localStorage.setItem(LAST_REPORT_KEY, String(Date.now()));
      })
      .catch(() => {
        // Device reporting is observational only; never interrupt gameplay.
      });
  }, REPORT_DEFER_MS);
}

function collectDeviceInfo(): Record<string, unknown> {
  const nav = window.navigator;
  const ua = nav.userAgent || '';
  const standalone = window.matchMedia('(display-mode: standalone)').matches
    || Boolean((nav as Navigator & { standalone?: boolean }).standalone);
  const width = window.innerWidth || document.documentElement.clientWidth || 0;
  const height = window.innerHeight || document.documentElement.clientHeight || 0;
  return {
    device_key: getDeviceKey(),
    platform: nav.platform || null,
    browser: detectBrowser(ua),
    os: detectOs(ua),
    device_type: detectDeviceType(ua, width),
    display_mode: standalone ? 'pwa' : 'browser',
    screen_width: window.screen?.width || null,
    screen_height: window.screen?.height || null,
    viewport_width: width,
    viewport_height: height,
    dpr: window.devicePixelRatio || 1,
    orientation: height > width ? 'portrait' : 'landscape',
    touch: nav.maxTouchPoints > 0 || 'ontouchstart' in window,
    max_touch_points: nav.maxTouchPoints || 0,
    language: nav.language || null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    user_agent: ua,
  };
}

function getDeviceKey(): string {
  const existing = window.localStorage.getItem(DEVICE_KEY);
  if (existing) return existing;
  const key = crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.localStorage.setItem(DEVICE_KEY, key);
  return key;
}

function detectBrowser(ua: string): string {
  if (/Edg\//.test(ua)) return 'Edge';
  if (/CriOS|Chrome\//.test(ua)) return 'Chrome';
  if (/FxiOS|Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Unknown';
}

function detectOs(ua: string): string {
  if (/iPad|iPhone|iPod/.test(ua)) return 'iOS/iPadOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Unknown';
}

function detectDeviceType(ua: string, width: number): string {
  if (/iPad|Tablet/.test(ua)) return 'tablet';
  if (/Mobi|iPhone|Android/.test(ua) && width < 760) return 'phone';
  if (width >= 760 && width < 1200 && ('ontouchstart' in window || navigator.maxTouchPoints > 0)) return 'tablet';
  return 'desktop';
}
